import { DrawProduct, View } from "@novorender/api";
import { ReadonlyVec3 } from "gl-matrix";
import { MutableRefObject, useCallback, useEffect, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { Canvas2D } from "components";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useLastPickSample } from "contexts/lastPickSample";
import { drawPart, getCameraState } from "features/engine2D";
import { selectPointLineMode } from "features/mobileMeasure/mobileMeasureSlice";
import { Picker, selectPicker } from "features/render";

import { usePointToPointAxisDrawProduct } from "./hooks/usePointToPointAxisDrawProduct";
import {
    selectDraggingPoint,
    selectPointLineCurrentIdx,
    selectPointLines,
    selectPointLineSnapToPoint,
    selectShowPointAngleOnCanvas,
} from "./pointLineSlice";
import { getPointLineWithDrag, isPointLineClosed } from "./utils";

const lineColor = "#00DB2C";

/** Placeholder id for the cursor preview point—never stored, only used for drawing. */
const PREVIEW_POINT_ID = "__preview__";

export function PointLineCanvas({
    renderFnRef,
    svg: _svg,
}: {
    renderFnRef: MutableRefObject<((moved: boolean) => void) | undefined>;
    svg: SVGSVGElement | null;
}) {
    const {
        state: { view, size },
    } = useExplorerGlobals();
    const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);

    const pointLines = useAppSelector(selectPointLines);
    const currentIndex = useAppSelector(selectPointLineCurrentIdx);
    const draggingPoint = useAppSelector(selectDraggingPoint);
    const picker = useAppSelector(selectPicker);
    const isPointLinePicker = picker === Picker.PointLine;
    const lastPickSampleRaw = useLastPickSample();
    const lastPickSample = isPointLinePicker ? lastPickSampleRaw : null;
    const showPointAngleOnCanvas = useAppSelector(selectShowPointAngleOnCanvas);
    const snapTo = useAppSelector(selectPointLineSnapToPoint);
    const ptLineMode = useAppSelector(selectPointLineMode);

    const axisDrawProducts = usePointToPointAxisDrawProduct(view, pointLines);

    const draw = useCallback(() => {
        if (!view?.measure || !ctx || !canvas) {
            return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const cameraState = getCameraState(view.renderState.camera);
        pointLines.forEach((line, lineIndex) => {
            let pts = line.points;

            // Start and end indexes of dashed line, both inclusive
            // Start index can be greater than end index, when the dash is around the first point
            let dashLineRange: [number, number] | undefined;

            // When placing a new point - draw line to cursor (pick position more precisely) from the last point.
            // If snapped to a point - use that point position.
            const drawLineToCursor =
                isPointLinePicker &&
                !draggingPoint &&
                pts.length > 0 &&
                lastPickSample &&
                lineIndex === currentIndex &&
                ptLineMode !== "moving" &&
                ptLineMode !== "selected";
            if (drawLineToCursor) {
                dashLineRange = [line.points.length - 1, line.points.length];
                const position =
                    lineIndex === currentIndex && snapTo !== null
                        ? line.points[snapTo].position
                        : (lastPickSample.hoverEntity?.connectionPoint ?? lastPickSample.position);
                if (position) {
                    pts = [
                        ...pts,
                        {
                            id: PREVIEW_POINT_ID,
                            position,
                            snap: { type: "vertex", connection: position },
                        },
                    ];
                }
            }

            if (pts.length <= 1) {
                return;
            }

            const isClosed = isPointLineClosed(line.points);

            // Get points positions.
            // If a point in this line is being dragged - return drag position.
            let positions: ReadonlyVec3[];
            const lastPickPos = lastPickSample?.hoverEntity?.connectionPoint ?? lastPickSample?.position;
            if (lineIndex === draggingPoint?.pointLineIdx && lastPickPos) {
                positions = getPointLineWithDrag(line, isClosed, draggingPoint.pointIdx, lastPickPos, snapTo).map(
                    (p) => p.position,
                );

                const dashStart =
                    isClosed && draggingPoint.pointIdx === 0
                        ? positions.length - 2
                        : Math.max(0, draggingPoint.pointIdx - 1);
                const dashEnd =
                    isClosed && draggingPoint.pointIdx === positions.length - 1
                        ? 1
                        : Math.min(positions.length - 1, draggingPoint.pointIdx + 1);
                dashLineRange = [dashStart, dashEnd];
            } else {
                positions = pts.map((p) => p.position);
            }

            // Lines and angles are drawn separately, because there are issues with angle at point 0 in closed lines (closed=true):
            // getDrawObjectFromPoints creates "filled" part in this case, which uses closePath to draw the last line and then calls ctx.fill(),
            // it's not compatible with the way lineDashIndexMask is implemented - it starts new paths, so the fill won't fill the proper area.
            // We could make a separate path for filling, but it might affect performance for some other consumers like arcgis widget.

            // Split line into segments of dashed and regular lines
            const segments = getDashedAndRegularSegments(positions, dashLineRange);

            // Draw main lines
            for (const { positions, dashed } of segments) {
                view.measure?.draw
                    .getDrawObjectFromPoints(positions, {
                        closed: false,
                        angles: false,
                        generateLengthLabels: false,
                    })
                    ?.objects.forEach((obj) =>
                        obj.parts.forEach((part) => {
                            drawPart(
                                ctx,
                                cameraState,
                                part,
                                {
                                    lineColor,
                                    pointColor: { start: "green", middle: "white", end: "blue" },
                                    displayAllPoints: false,
                                    lineDash: dashed ? [5, 5] : undefined,
                                },
                                2,
                                {
                                    type: "default",
                                },
                            );
                        }),
                    );
            }

            // Draw angles
            if (showPointAngleOnCanvas) {
                if (isClosed) {
                    // If closed - add second point to the end so the angle at point 0 is drawn.
                    positions.push(positions[1]);
                }

                view.measure?.draw
                    .getDrawObjectFromPoints(positions, {
                        closed: false,
                        angles: true,
                        generateLengthLabels: false,
                    })
                    ?.objects.forEach((obj) =>
                        obj.parts.forEach((part) => {
                            if (part.drawType !== "angle") {
                                return;
                            }
                            drawPart(
                                ctx,
                                cameraState,
                                part,
                                {
                                    lineColor,
                                    pointColor: { start: "green", middle: "white", end: "blue" },
                                    displayAllPoints: false,
                                },
                                2,
                                {
                                    type: "default",
                                    drawAngleText: false,
                                },
                            );
                        }),
                    );
            }
        });

        // Draw axis lines
        axisDrawProducts.forEach((axisDrawProduct, i) => {
            if (!axisDrawProduct || draggingPoint?.pointLineIdx === i) {
                return;
            }

            drawAxisLines(view, ctx, cameraState, axisDrawProduct);
        });
    }, [
        view,
        pointLines,
        ctx,
        canvas,
        draggingPoint,
        lastPickSample,
        showPointAngleOnCanvas,
        axisDrawProducts,
        isPointLinePicker,
        currentIndex,
        snapTo,
        ptLineMode,
    ]);

    useEffect(() => {
        draw();
    }, [draw, size]);

    useEffect(() => {
        renderFnRef.current = animate;
        return () => (renderFnRef.current = undefined);

        async function animate(moved: boolean): Promise<void> {
            if (!view || !moved) {
                return;
            }

            draw();
        }
    }, [view, renderFnRef, draw]);

    const canDraw = pointLines.some((ptLine) => ptLine.points.length > 0);
    return (
        <>
            {canDraw && (
                <Canvas2D
                    id="point-line-canvas"
                    data-include-snapshot
                    ref={(el) => {
                        setCanvas(el);
                        setCtx(el?.getContext("2d") ?? null);
                    }}
                    width={size.width}
                    height={size.height}
                />
            )}
        </>
    );
}

function drawAxisLines(
    view: View,
    ctx: CanvasRenderingContext2D,
    cameraState: ReturnType<typeof getCameraState>,
    drawProduct: DrawProduct,
) {
    view.measure?.draw.updateProduct(drawProduct);

    drawProduct.objects.forEach((obj) => {
        obj.parts.forEach((part) => {
            let color: string | undefined;
            switch (part.name) {
                case "x-axis":
                    color = "#F00";
                    break;
                case "y-axis":
                    color = "#005DFF";
                    break;
                case "z-axis":
                    color = "#E5FF00";
                    break;
            }
            if (!color) {
                return;
            }

            drawPart(
                ctx,
                cameraState,
                part,
                {
                    lineColor: color,
                    displayAllPoints: false,
                    lineDash: [5, 5],
                },
                2,
                {
                    type: "default",
                },
            );
        });
    });
}

function getDashedAndRegularSegments(positions: ReadonlyVec3[], dashLineRange?: [number, number]) {
    const segments: { positions: ReadonlyVec3[]; dashed: boolean }[] = [];

    if (!dashLineRange) {
        segments.push({ positions, dashed: false });
    } else {
        const [dashStart, dashEnd] = dashLineRange;

        if (dashStart > dashEnd) {
            // When dash line is around the first point
            segments.push({ positions: positions.slice(dashEnd, dashStart + 1), dashed: false });
            segments.push({
                positions: [...positions.slice(dashStart), ...positions.slice(0, dashEnd + 1)],
                dashed: true,
            });
        } else {
            if (dashStart > 0) {
                // Regular segment before the dash
                segments.push({ positions: positions.slice(0, dashStart + 1), dashed: false });
            }
            if (dashEnd < positions.length - 1) {
                // Regular segment after the dash
                segments.push({ positions: positions.slice(dashEnd), dashed: false });
            }
            // Dashed segment
            segments.push({ positions: positions.slice(dashStart, dashEnd + 1), dashed: true });
        }
    }

    return segments;
}
